import { useRef } from 'react';
import { Controller } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { clsx } from 'clsx';
import EmptyList from 'components/EmptyList/EmptyList';
import Loader from 'components/Loader/Loader';
import Panel from 'components/Panel/Panel';
import Table from 'components/Table/Table';
import TableBody from 'components/Table/TableBody';
import TableCell from 'components/Table/TableCell';
import TableHead from 'components/Table/TableHead';
import TableRow from 'components/Table/TableRow';
import TableSort from 'components/Table/TableSort';
import { SORT } from 'constants/formFields';

import { MATERIALITY_ASSESSMENT_SURVEY_HEAD_CELLS } from '../../constants/dataSets';
import { getTextDate } from '../../utils/formattedData';
import TableMAQuestionActions from './TableMAQuestionActions';

import type { FieldValues, Path } from 'react-hook-form';
import type ITableMASurveyProps from '../../pages/MaterialAssessmentPages/MaterialAssessmentSurveyInterview/ITableMASurveyProps';
import type IQuestionsItem from '../../store/slices/survey/interfaces/IQuestionsItem';

import styles from './scss/TableMaterialityAssessmentQuestion.module.scss';

function TableMAQuestion<TForm extends FieldValues>(props: ITableMASurveyProps<TForm>) {
  const { questionProps, vcOptions, disableActions } = props;
  const { t } = useTranslation();

  const emptyListText = (
    <Trans
      i18nKey="common.noResultsWithFilters"
      t={t}
      components={{
        br: <br />,
      }}
    />
  );

  const tableRef = useRef<HTMLDivElement>(null);

  return (
    <Panel className={styles.panel}>
      <div
        className={clsx(styles.loaderWrap, questionProps.isLoading && styles.loaderWrapActive)}
        aria-hidden={!questionProps.isLoading}
      >
        <Loader
          isPageLoading={questionProps.isLoading}
          isFullLoader={false}
          classNameLoader={styles.loaderCentered}
        />
      </div>

      {!questionProps.isLoading
        && (questionProps.items.length > 0 && MATERIALITY_ASSESSMENT_SURVEY_HEAD_CELLS ? (
          <InfiniteScroll
            className={styles.infiniteScroll}
            dataLength={questionProps.items?.length || 0}
            next={questionProps.loadMoreQuestions}
            hasMore={questionProps.hasMore}
            loader={false}
          >
            <div ref={tableRef} className={styles.tableBodyScroll}>
              <Table className={styles.table}>
                <TableHead className={styles.header}>
                  {MATERIALITY_ASSESSMENT_SURVEY_HEAD_CELLS.map((col) => {
                    if (col.id === 'actions' && disableActions) {
                      return null;
                    }
                    return (
                      col.withSort ? (
                        <Controller
                          key={col.id}
                          name={SORT as Path<TForm>}
                          control={questionProps.control}
                          render={({ field: { value } }) => (
                            <TableCell head className={styles.tableCell}>
                              <TableSort
                                active={col.id === value?.orderKey}
                                onClickSort={() => questionProps.onSortChange && questionProps.onSortChange(col.id)}
                              >
                                <span>{t(col?.label || '')}</span>
                              </TableSort>
                            </TableCell>
                          )}
                        />
                      ) : (
                        <TableCell head className={styles.tableCell} key={col.id}>
                          <div>
                            <span>{t(col?.label || '')}</span>
                          </div>
                        </TableCell>
                      )
                    );
                  })}
                </TableHead>
                <TableBody className={styles.body}>
                  {!questionProps.isLoading && questionProps.items?.map((item: IQuestionsItem) => (
                    <TableRow key={[item.id, item.name].join('_')}>
                      <TableCell>
                        <span className={styles.nameCell}>{item?.name}</span>
                      </TableCell>
                      <TableCell>
                        <span className={styles.description}>{item?.description}</span>
                      </TableCell>
                      <TableCell>
                        <div className={styles.createdCell}>
                          <span className={styles.titleCreated}>{getTextDate(item?.updatedAt)}</span>
                          {item?.updatedBy?.firstName ? (
                            <span className={styles.nameCreated}>
                              {`by ${item.updatedBy.firstName} ${item.updatedBy.lastName}`}
                            </span>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className={styles.createdCell}>
                          <span className={styles.titleCreated}>{getTextDate(item?.updatedAt)}</span>
                          {item?.updatedBy?.firstName ? (
                            <span className={styles.nameCreated}>
                              {`by ${item.updatedBy.firstName} ${item.updatedBy.lastName}`}
                            </span>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={styles.question}>{item?.questionsCount}</span>
                      </TableCell>
                      <TableCell>
                        <div className={styles.statusCell}>
                          <span className={`${styles.statusText} ${styles[item?.status]}`}>
                            <span>{t(`materialityAssessment.statusOptions.${item?.status}`)}</span>
                          </span>
                        </div>
                      </TableCell>
                      {!disableActions && (
                      <TableCell>
                        <div className={styles.actionCell}>
                          <TableMAQuestionActions
                            questionItem={item}
                            onShare={questionProps.onShare ? questionProps.onShare : undefined}
                            onDelete={questionProps.onDelete ? questionProps.onDelete : undefined}
                            onEdit={questionProps.onEdit ? questionProps.onEdit : undefined}
                            onViewMode={questionProps.onViewMode ? questionProps.onViewMode : undefined}
                            vcOptions={vcOptions}
                            disableActions={disableActions}
                          />
                        </div>
                      </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </InfiniteScroll>
        ) : (
          <EmptyList text={emptyListText} />
        ))}
    </Panel>
  );
}

export default TableMAQuestion;
